import { useState, useEffect, useCallback } from "react";
import api from "../config/api";

/**
 * Custom hook for fetching and managing user profile data
 * Automatically fetches profile when token is available
 * Provides caching to avoid repeated API calls
 *
 * @returns {Object} - Hook state and functions
 * @returns {Object} returns.profile - User profile data
 * @returns {boolean} returns.loading - Loading state during API call
 * @returns {string|null} returns.error - Error message if API call fails
 * @returns {Function} returns.refetchProfile - Function to manually refresh profile data
 * @returns {boolean} returns.isAuthenticated - Whether user has valid token
 */
const useUserProfile = () => {
  const [profile, setProfile] = useState(null);
  
  const [loading, setLoading] = useState(false);
  
  const [error, setError] = useState(null);

  /**
   * Get authentication token from localStorage
   * @returns {string|null} - JWT token or null if not found
   */
  const getToken = useCallback(() => {
    try {
      return localStorage.getItem("authToken");
    } catch (err) {
      // Silently handle localStorage access errors
      return null;
    }
  }, []);

  /**
   * Fetches user profile from API
   * Makes GET request to /api/v1/users/profile endpoint
   *
   * @returns {Promise<Object|null>} - API response data or null if error occurs
   */
  const fetchProfile = useCallback(async () => {
    const token = getToken();
    
    // Don't fetch if no token is available
    if (!token) {
      // Clear profile data and error state when no token
      setProfile(null);
      setError(null);
      setLoading(false);
      return null;
    }

    // Set loading state and clear previous errors
    setLoading(true);
    setError(null);

    try {
      // Make API call to fetch user profile
      const response = await api.get("/users/profile");

      // Store response data in state and stop loading
      setProfile(response.data);
      setLoading(false);

      // Return response data for immediate use
      return response.data;
    } catch (err) {
      // Extract error message from response or use default error message
      const errorMessage =
        err.response?.data?.message || "Failed to fetch user profile";

      // Store error message and stop loading
      setError(errorMessage);
      setLoading(false);

      // Return null to indicate failure
      return null;
    }
  }, [getToken]); // Recreate function only when getToken changes

  /**
   * Auto-fetch profile when component mounts
   * Only fetches if token is available
   * Dependency array ensures this runs only when fetchProfile changes
   */
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Check if user is authenticated (has token)
  // Uses double negation to convert token to boolean
  const isAuthenticated = !!getToken();

  // Return hook state and utility functions for component consumption
  return {
    profile, // User profile data from API response
    loading, // Current loading state during API requests
    error, // Current error state with API failure messages
    refetchProfile: fetchProfile, // Function to manually refresh profile data
    isAuthenticated, // Boolean flag indicating if user has valid token
  };
};

export default useUserProfile;
